/**
 * Central Configuration File for my-skin.ai Dermatology Consultation App
 * Single source of truth for all application settings
 */

export const config = {
  // AI Configuration (SECURE - Server-side only)
  ai: {
    // Security: API key removed from client-side config
    // All AI processing moved to secure server endpoint
    systemPrompt: `You are my-skin.ai dermatology system. Analyze skin in image and return ONLY this JSON format:
{"patientAnalysis":{"skinType":"normal/dry/oily/combination","skinSensitivity":"none/low/moderate/high"},"detailedAnalysis":{"darkCircles":{"percentage":0-100,"severity":"low/moderate/high","description":"text"},"eyeBags":{"percentage":0-100,"severity":"low/moderate/high","description":"text"},"wrinkles":{"percentage":0-100,"severity":"low/moderate/high","description":"text"},"deepWrinkles":{"percentage":0-100,"severity":"low/moderate/high","description":"text"},"pores":{"percentage":0-100,"severity":"low/moderate/high","description":"text"},"pigmentation":{"percentage":0-100,"severity":"low/moderate/high","description":"text"},"acnes":{"percentage":0-100,"severity":"low/moderate/high","description":"text"},"eyeWrinkles":{"percentage":0-100,"severity":"low/moderate/high","description":"text"}},"overallAssessment":{"condition":"text","severity":"Mild/Moderate/Severe","primaryConcerns":["list"],"skinAge":"text"},"recommendations":{"treatment":"text","followup":"text","skincare":"text"},"summary":"text","disclaimer":"This analysis is by my-skin.ai for informational purposes only. Consult a dermatologist for medical diagnosis."}

Return only valid JSON, no markdown or extra text.`,
    maxTokens: 4000
  },

  // PDF Report Configuration
  pdf: {
    companyName: 'my-skin.ai',
    logoPath: '/assets/LOGO.svg',
    template: 'medical-report',
    fields: ['patientName', 'patientAge', 'patientEmail', 'patientPhone', 'date', 'condition', 'severity', 'treatment', 'followup'],
    disclaimer: 'This report is generated by an AI system for informational purposes only. It is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of a qualified healthcare provider with questions regarding medical conditions.',
    reportTitle: 'AI-Assisted Dermatology Analysis Report',
    pageFormat: 'a4',
    margins: {
      top: 20,
      right: 20,
      bottom: 20,
      left: 20
    }
  },

  // WhatsApp Integration
  whatsapp: {
    apiEndpoint: process.env.REACT_APP_WHATSAPP_API,
    businessNumber: process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER,
    messageTemplate: {
      greeting: 'Hello! I would like to book an appointment.',
      referral: 'I was referred by my-skin.ai.',
      patientInfo: 'Patient Name: {patientName}',
      analysisInfo: 'AI Analysis Summary: {condition} (Severity: {severity})',
      request: 'Could you please help me schedule a consultation?'
    }
  },

  // Doctor Database Configuration (MongoDB)
  doctors: {
    databaseUrl: process.env.REACT_APP_MONGODB_URI,
    databaseName: 'dermatology_app',
    collection: 'partner_doctors',
    searchRadius: 25, // kilometers
    maxResults: 20,
    fallbackToGooglePlaces: false, // Using only partner doctors
    commissionRate: 0.15, // 15% commission on bookings
    requiredFields: ['name', 'clinic_name', 'address', 'phone', 'whatsapp_number', 'latitude', 'longitude', 'commission_rate']
  },

  // Privacy and Data Handling
  privacy: {
    dataRetention: '30 days',
    anonymization: true,
    encryptionRequired: true,
    notice: 'We prioritize your privacy. Medical images are not stored. With your explicit consent, patient information is encrypted and stored securely for appointment booking and follow-up purposes only.',
    consentRequired: true
  },

  // Application Settings
  app: {
    name: 'my-skin.ai Skin Analysis',
    version: '1.0.0',
    supportEmail: 'support@my-skin.ai',
    maxImageSize: 5 * 1024 * 1024, // 5MB in bytes
    allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png'],
    sessionTimeout: 30 * 60 * 1000, // 30 minutes in milliseconds
    defaultTheme: 'medical'
  },

  // UI Configuration
  ui: {
    theme: {
      primary: '#2563eb', // Blue
      secondary: '#1e40af',
      accent: '#3b82f6',
      background: '#ffffff',
      surface: '#f8fafc',
      text: '#1f2937',
      textSecondary: '#6b7280',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444'
    },
    layout: {
      maxWidth: '1200px',
      cardPadding: '24px',
      borderRadius: '8px',
      shadowLevel: 'medium'
    },
    animations: {
      transition: '0.2s ease-in-out',
      loadingDuration: '1.5s'
    }
  },

  // API Configuration
  api: {
    timeout: 120000, // 2 minutes (increased for AI analysis)
    retryAttempts: 3,
    retryDelay: 1000, // 1 second
    baseHeaders: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  },

  // Geolocation Settings
  location: {
    timeout: 10000, // 10 seconds
    maximumAge: 300000, // 5 minutes
    enableHighAccuracy: true,
    defaultLocation: {
      lat: 28.6139, // New Delhi coordinates as fallback
      lng: 77.2090
    }
  },

  // Error Messages
  errors: {
    network: 'Network error. Please check your internet connection and try again.',
    imageUpload: 'Failed to upload image. Please ensure the file is under 5MB and in JPG or PNG format.',
    aiAnalysis: 'AI analysis failed. Please try again or contact support.',
    pdfGeneration: 'Failed to generate PDF report. Please try again.',
    location: 'Unable to access your location. Please enable location services or enter your address manually.',
    doctorSearch: 'No doctors found in your area. Please try expanding your search radius.',
    whatsappBooking: 'Failed to open WhatsApp. Please ensure WhatsApp is installed on your device.'
  },

  // Feature Flags
  features: {
    cameraCapture: true,
    fileUpload: true,
    locationServices: true,
    pdfDownload: true,
    whatsappBooking: true,
    doctorFinder: true,
    multiLanguageSupport: false,
    analytics: true
  }
};

// Environment validation (CLIENT-SIDE ONLY)
export const validateConfig = () => {
  // Security: No client-side environment validation required
  // All sensitive credentials are handled server-side only
  
  // Optional: Check for public configuration if needed
  const optionalEnvVars = [
    // Add any truly public env vars here if needed
  ];

  // Always return true - no required client-side env vars
  return true;
};

// Helper function to get configuration value safely
export const getConfigValue = (path, defaultValue = null) => {
  try {
    return path.split('.').reduce((obj, key) => obj[key], config) || defaultValue;
  } catch (error) {
    return defaultValue;
  }
};

export default config;
